<template>
  <div class="production-steps-section">
    <h4>{{ t('Etapas de Produção') }}</h4>
    <div class="steps-list">
      <div
        v-for="(step, index) in steps"
        :key="step._key"
        class="step-row"
        :class="{
          'dragging': dragIndex === index,
          'drag-over': dragOverIndex === index,
          'step-terminal': !!step.is_terminal,
          'drop-forbidden': dragIndex !== null && dragOverIndex === index && !isDropAllowed(index)
        }"
        :data-testid="'so-production-steps-picker-row-' + index"
        :data-terminal="step.is_terminal ? '1' : '0'"
        draggable="true"
        @dragstart="onDragStart(index, $event)"
        @dragover.prevent="onDragOver(index)"
        @dragenter.prevent="onDragOver(index)"
        @dragleave="onDragLeave(index)"
        @drop.prevent="onDrop(index)"
        @dragend="onDragEnd"
      >
        <span class="step-drag-handle" :title="t('Arrastar para reordenar')">
          <i class="pi pi-bars"></i>
        </span>
        <span class="step-order">{{ index + 1 }}.</span>
        <select-list
          :elem-id="elemIdPrefix + '_' + index"
          :data-testid="'so-production-steps-picker-select-' + index"
          v-model="step.so_production_step_id"
          :itens-select="stepOptions"
          :placeholder="t('Selecione uma etapa')"
          :auto-select-first="false"
          style="flex: 1;"
        />
        <input-toggle
          :elem-id="elemIdPrefix + '_terminal_' + index"
          :data-testid="'so-production-steps-picker-terminal-' + index"
          :label="t('Fim de linha')"
          :model-value="!!step.is_terminal"
          :title="t('Etapa de fechamento: fica no fim do fluxo e serviços adicionados depois entram antes dela')"
          @update:model-value="setTerminal(index, $event)"
        />
        <app-button
          icon="pi pi-trash"
          severity="danger"
          text
          rounded
          size="small"
          :data-testid="'so-production-steps-picker-remove-btn-' + index"
          @click="removeStep(index)"
        />
      </div>
    </div>
    <app-button
      :label="t('Adicionar Etapa')"
      icon="pi pi-plus"
      severity="secondary"
      size="small"
      outlined
      data-testid="so-production-steps-picker-add-btn"
      @click="addStep"
      style="margin-top: 8px;"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOProductionSteps } from '../../composables/useSOProductionSteps';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

type EntityType = 'service' | 'additional_service';

interface Props {
  entityId: Id;
  entityType: EntityType;
  franchiseId?: Id;
  elemIdPrefix?: string;
}

interface StepRow {
  so_production_step_id: string | null;
  /**
   * End-of-line marker for THIS flow (so_service_step_is_terminal /
   * so_additional_service_step_is_terminal). Terminal steps form a contiguous
   * block at the END of the flow: a service added later has its steps inserted
   * immediately BEFORE the first terminal one, so painting never lands after
   * "Finalização" / "Controle de qualidade".
   */
  is_terminal: boolean;
  _key: number;
}

interface StepOption {
  ID: string;
  Name: string;
}

interface KVItem {
  value: Id;
  label: string;
}

interface FetchedStep {
  so_service_step_production_step_id?: Id;
  so_additional_service_step_production_step_id?: Id;
  [key: string]: unknown;
}

const props = withDefaults(defineProps<Props>(), {
  franchiseId: undefined,
  elemIdPrefix: 'soStep'
});

const emit = defineEmits<{
  'steps-changed': [];
}>();

const { t } = useI18n();
const {
  fetchKV: fetchStepsKV,
  fetchServiceSteps,
  fetchAdditionalServiceSteps,
  saveServiceSteps,
  saveAdditionalServiceSteps
} = useSOProductionSteps();

const stepOptions = ref<StepOption[]>([]);
const steps = ref<StepRow[]>([]);
let keyCounter = 0;

// Drag state
const dragIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

const onDragStart = (index: number, event: DragEvent): void => {
  dragIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
  }
};

const onDragOver = (index: number): void => {
  if (dragIndex.value === null || dragIndex.value === index) return;
  dragOverIndex.value = index;
};

const onDragLeave = (index: number): void => {
  if (dragOverIndex.value === index) {
    dragOverIndex.value = null;
  }
};

/**
 * Index of the first terminal step, or steps.length when none is marked.
 * Everything from here on is the terminal block; everything before it is the
 * body of the flow.
 */
const firstTerminalIndex = (): number => {
  const i = steps.value.findIndex(s => s.is_terminal);
  return i === -1 ? steps.value.length : i;
};

/**
 * The terminal block is not crossable. A body step may not land inside or
 * after it, and a terminal step may not land above a body step — but terminals
 * reorder freely AMONG THEMSELVES, which is how "Finalização comes before
 * Controle de qualidade" gets declared. Mirrors the normalization the backend
 * re-applies on save; the UI just refuses the move instead of silently
 * producing a flow the backend would rewrite.
 */
const isDropAllowed = (target: number): boolean => {
  const from = dragIndex.value;
  if (from === null || from === target) return false;

  const dragged = steps.value[from];
  const anchor = firstTerminalIndex();

  if (dragged.is_terminal) {
    // Removing the dragged row shifts everything after it left by one, so the
    // first body/terminal boundary the target must respect is computed on the
    // list WITHOUT the dragged item.
    const anchorWithoutDragged = from === anchor ? nextTerminalAfter(from) : anchor;
    return target >= anchorWithoutDragged;
  }
  return target < anchor;
};

/** First terminal index ignoring `skip`, or steps.length when there is none. */
const nextTerminalAfter = (skip: number): number => {
  for (let i = 0; i < steps.value.length; i++) {
    if (i !== skip && steps.value[i].is_terminal) return i > skip ? i - 1 : i;
  }
  return steps.value.length - 1;
};

const onDrop = (index: number): void => {
  if (dragIndex.value === null || dragIndex.value === index) return;
  if (!isDropAllowed(index)) {
    log('SOProductionStepsPicker', 'onDrop', 'Blocked by terminal block', { from: dragIndex.value, to: index });
    dragIndex.value = null;
    dragOverIndex.value = null;
    return;
  }
  const item = steps.value.splice(dragIndex.value, 1)[0];
  steps.value.splice(index, 0, item);
  log('SOProductionStepsPicker', 'onDrop', 'Reordered', { from: dragIndex.value, to: index });
  dragIndex.value = null;
  dragOverIndex.value = null;
};

/**
 * Marking a step as end-of-line MOVES it into the terminal block (keeping the
 * relative order), instead of leaving a terminal stranded mid-flow. Unmarking
 * drops it back to the end of the body, right before the block.
 */
const setTerminal = (index: number, value: boolean): void => {
  const [row] = steps.value.splice(index, 1);
  row.is_terminal = value;
  if (value) {
    steps.value.push(row);
  } else {
    steps.value.splice(firstTerminalIndex(), 0, row);
  }
  log('SOProductionStepsPicker', 'setTerminal', 'Terminal flag changed', { index, value });
};

const onDragEnd = (): void => {
  dragIndex.value = null;
  dragOverIndex.value = null;
};

const loadOptions = async (): Promise<void> => {
  try {
    const kvData = await fetchStepsKV() as KVItem[];
    stepOptions.value = kvData.map((item: KVItem) => ({ ID: String(item.value), Name: item.label }));
  } catch (err) {
    logError('SOProductionStepsPicker', 'loadOptions', 'Error', err);
    stepOptions.value = [];
  }
};

const loadSteps = async (): Promise<void> => {
  try {
    const entityIdNum = typeof props.entityId === 'string'
      ? parseInt(props.entityId, 10)
      : props.entityId;

    const fetched = props.entityType === 'service'
      ? await fetchServiceSteps(entityIdNum) as unknown as FetchedStep[]
      : await fetchAdditionalServiceSteps(entityIdNum) as unknown as FetchedStep[];

    const fkField = props.entityType === 'service'
      ? 'so_service_step_production_step_id'
      : 'so_additional_service_step_production_step_id';

    const terminalField = props.entityType === 'service'
      ? 'so_service_step_is_terminal'
      : 'so_additional_service_step_is_terminal';

    steps.value = fetched.map((s: FetchedStep) => ({
      so_production_step_id: String(s[fkField]),
      is_terminal: Number(s[terminalField] ?? 0) === 1,
      _key: ++keyCounter
    }));
  } catch (err) {
    logError('SOProductionStepsPicker', 'loadSteps', 'Error', err);
    steps.value = [];
  }
};

const addStep = (): void => {
  // A new step is body, never end-of-line, so it goes right before the
  // terminal block instead of after it.
  steps.value.splice(firstTerminalIndex(), 0, {
    so_production_step_id: null,
    is_terminal: false,
    _key: ++keyCounter
  });
};

const removeStep = (index: number): void => {
  steps.value.splice(index, 1);
};

const saveSteps = async (): Promise<void> => {
  const payload = steps.value
    .filter(s => s.so_production_step_id)
    .map((s, i) => ({
      production_step_id: s.so_production_step_id,
      sort_order: i + 1,
      is_terminal: s.is_terminal ? 1 : 0
    }));

  // Note: props.franchiseId is intentionally accepted by the parent contract but
  // useSOProductionSteps' save APIs do not currently require it.
  void props.franchiseId;

  if (props.entityType === 'service') {
    await saveServiceSteps(props.entityId, payload);
  } else {
    await saveAdditionalServiceSteps(props.entityId, payload);
  }
};

onMounted(async () => {
  log('SOProductionStepsPicker', 'onMounted', 'Loading', { entityId: props.entityId, entityType: props.entityType });
  await loadOptions();
  await loadSteps();
});

defineExpose({ saveSteps, loadSteps });
</script>

<style scoped>
.production-steps-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-200);
}

.production-steps-section h4 {
  margin-bottom: 12px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 2px solid transparent;
  background: var(--p-surface-0);
  transition: border-color 0.15s, background 0.15s, opacity 0.15s;
}

.step-row.dragging {
  opacity: 0.4;
}

.step-row.drag-over {
  border-color: var(--p-primary-color);
  background: var(--p-primary-50);
}

.step-drag-handle {
  cursor: grab;
  color: var(--p-text-muted-color);
  padding: 4px;
  display: flex;
  align-items: center;
}

.step-drag-handle:active {
  cursor: grabbing;
}

.step-order {
  font-weight: 600;
  min-width: 24px;
  color: var(--p-text-muted-color);
}
</style>
